import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { motion } from "framer-motion";
import { company, stats } from "../../../../utils/siteContent";
import CountUp from "../../../commons/countUp/countUp";
import { StyledHero } from "../../../styled/hero";
import { StyledLayout } from "../../../styled/layout";

const MotionEyebrow = motion(StyledHero.Eyebrow);
const MotionTitle = motion(StyledHero.Title);
const MotionLead = motion(StyledHero.Lead);
const MotionActions = motion(StyledHero.Actions);
const MotionPills = motion(StyledHero.Pills);
const MotionVisual = motion(StyledHero.Visual);
const MotionStats = motion(StyledHero.Stats);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Onda de electrocardiograma: un ciclo PQRST de 280 unidades de ancho que
// vuelve siempre a la línea base, repetido para poder desplazarlo sin cortes.
const ECG_CYCLE =
  "h60 l12 -10 l12 10 h26 l8 8 l10 -78 l10 92 l8 -22 h18 l16 -20 l16 20 h84";
const ECG_PATH = `M0 120 ${Array(6).fill(ECG_CYCLE).join(" ")}`;

const Hero = () => (
  <StyledHero.Root id="inicio">
    <StyledHero.Bg aria-hidden="true">
      <StyledHero.Blob variant={1} />
      <StyledHero.Blob variant={2} />
      <StyledHero.Blob variant={3} />
      <StyledHero.Grid />
    </StyledHero.Bg>

    <StyledLayout.Container>
      <StyledHero.Inner>
        <StyledHero.Content>
          <MotionEyebrow custom={0} variants={fadeUp} initial="hidden" animate="show">
            <StyledHero.Pulse /> Desde {company.founded} · +{company.yearsExperience}{" "}
            años liderando el mercado
          </MotionEyebrow>

          <MotionTitle custom={1} variants={fadeUp} initial="hidden" animate="show">
            Software integral de{" "}
            <StyledLayout.GradientText>Medicina Laboral</StyledLayout.GradientText> y
            Accidentología
          </MotionTitle>

          <MotionLead custom={2} variants={fadeUp} initial="hidden" animate="show">
            {company.hero}
          </MotionLead>

          <MotionActions custom={3} variants={fadeUp} initial="hidden" animate="show">
            <StyledLayout.Button href="#contacto" variant="primary">
              Solicitá asesoramiento <NorthEastIcon />
            </StyledLayout.Button>
            <StyledLayout.Button href="#que-hacemos" variant="ghost">
              Conocé nuestras soluciones
            </StyledLayout.Button>
          </MotionActions>

          <MotionPills custom={4} variants={fadeUp} initial="hidden" animate="show">
            <li>
              <CheckCircleIcon /> ISO 9001:2015
            </li>
            <li>
              <CheckCircleIcon /> Conectividad con ART, SRT y SSN
            </li>
            <li>
              <CheckCircleIcon /> Great Place to Work®
            </li>
          </MotionPills>
        </StyledHero.Content>

        {/* Latido (ECG) integrado */}
        <MotionVisual
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          <StyledHero.Ecg>
            <svg
              viewBox="0 0 560 240"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true">
              <defs>
                <linearGradient id="ecgGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#174F9B" />
                  <stop offset="0.5" stopColor="#2E6FD6" />
                  <stop offset="1" stopColor="#4BA3E0" />
                </linearGradient>
                <filter id="ecgGlow">
                  <feGaussianBlur stdDeviation="4" />
                </filter>
              </defs>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="-280 0"
                  dur="2.6s"
                  repeatCount="indefinite"
                />
                <path className="glow" d={ECG_PATH} />
                <path className="line" d={ECG_PATH} />
              </g>
            </svg>
          </StyledHero.Ecg>
        </MotionVisual>
      </StyledHero.Inner>
    </StyledLayout.Container>

    {/* Estadísticas */}
    <StyledLayout.Container>
      <MotionStats
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}>
        {stats.map((s) => (
          <StyledHero.Stat key={s.label}>
            <StyledHero.StatNum>
              <CountUp end={s.value} />
              {s.suffix}
            </StyledHero.StatNum>
            <StyledHero.StatLabel>{s.label}</StyledHero.StatLabel>
          </StyledHero.Stat>
        ))}
      </MotionStats>
    </StyledLayout.Container>
  </StyledHero.Root>
);

export default Hero;
